import { useMemo } from 'react';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NextLink,
  NormalizedCacheObject,
  Observable,
  Operation,
} from '@apollo/client';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { refreshUser, getUserAndTokenFromStorage } from '@firsttable/functions';
import {
  Logger,
  isStellateEndpoint,
  isNextProductionBuild,
  resolveGraphqlMutationEndpoint,
  isGraphqlMutationDocument,
} from '@firsttable/common';

interface ApolloConfig {
  key?: 'CLIENT' | 'SERVER';
  handlers?: {
    logoutHandler?: (client?: ApolloClient<any> | any) => Promise<any> | any;
  };
  endpoint?: string;
  mutationEndpoint?: string;
  origin?: string;
  preview?: boolean;
  bypassCache?: boolean;
}

const apolloClients: ApolloClient<NormalizedCacheObject>[] | [] = [];
const apolloClientTimers = [];

const apolloClientTTL = 10 * 60 * 1000; // 10 minutes

const logger = new Logger('Apollo');

function isMutationOperation(operation: Operation) {
  return isGraphqlMutationDocument(operation.query);
}

// See: https://www.apollographql.com/blog/apollo-client/next-js/building-a-next-js-app-with-slash-graphql/
function createApolloClient(
  config: ApolloConfig,
): ApolloClient<NormalizedCacheObject> {
  const {
    handlers: { logoutHandler },
  } = config;

  const httpLink: HttpLink = new HttpLink({
    uri: config.endpoint,
    // fetchOptions: {
    //     mode: process.env.NODE_ENV === 'development' ? 'no-cors' : 'cors'
    // }
  });
  const mutationHttpLink: HttpLink = new HttpLink({
    uri: resolveGraphqlMutationEndpoint(
      config.endpoint,
      config.mutationEndpoint,
    ),
  });

  const authLink: ApolloLink = new ApolloLink(
    (operation: Operation, forward: NextLink) => {
      const { token, cdnToken, user } = getUserAndTokenFromStorage();
      const targetEndpoint = isMutationOperation(operation)
        ? resolveGraphqlMutationEndpoint(
            config.endpoint,
            config.mutationEndpoint,
          )
        : config.endpoint;

      const authToken: Record<string, string> = {};
      if (user?.isLoggedIn && token) {
        authToken['Application-Authorization'] = `Bearer ${token}`;
        if (cdnToken) {
          authToken['cdn-token'] = `Bearer ${cdnToken}`;
        }
      }

      operation.setContext((context: any) => ({
        ...context,
        headers: {
          ...context.headers,
          ...authToken,
          accept: 'application/json',
          origin: config.origin || process.env.NEXT_PUBLIC_APOLLO_ORIGIN,
          stage: config.preview ? 'Stage' : 'Live',
          'x-graphql-client-name': 'Website',
          ...(process.env.NEXT_PUBLIC_WEBSITE_URL && {
            'x-graphql-client-version': process.env.NEXT_PUBLIC_WEBSITE_URL,
          }),
          ...(config.key === 'SERVER' &&
            isNextProductionBuild() &&
            isStellateEndpoint(targetEndpoint) &&
            process.env.STELLATE_RATE_LIMITING_SHARED_SECRET && {
              'x-graphql-rate-limiting-secret':
                process.env.STELLATE_RATE_LIMITING_SHARED_SECRET,
            }),
          ...(config.bypassCache && { 'x-bypass-cache': 'true' }),
        },
      }));
      return forward(operation);
    },
  );

  const retryLink = new RetryLink({
    delay: {
      initial: 1000, // 1s
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error, _operation) => {
        // Only retry during the Vercel build process (see https://vercel.com/docs/concepts/projects/environment-variables#system-environment-variables)
        if (typeof process.env.CI !== 'undefined') {
          console.log(
            `ApolloClient retrying query due to "${_operation.operationName}" due to: `,
            error,
          );
          return !!error;
        }
        return false;
      },
    },
  });
  // apollo link to decode @JSON directive
  const jsonLink = new ApolloLink((operation, forward) =>
    forward(operation).map((response) => {
      const context = operation.getContext();
      const {
        response: { data },
      } = context;
      if (data) {
        Object.keys(data).forEach((key) => {
          if (key.endsWith('JSON')) {
            data[key] = JSON.parse(data[key]);
          } else {
            const value = data[key];
            if (value && value.__typename === 'EncodedJSON') {
              data[key] = JSON.parse(value.value);
            }
          }
        });
      }
      return response;
    }),
  );

  const client = new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([
      authLink,
      onError(
        ({ graphQLErrors, networkError, operation, forward }: ErrorResponse) =>
          new Observable((observer: any) => {
            const { token } = getUserAndTokenFromStorage();
            const forwardOperation = (forwardOperation: any) => {
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };

              return forward(forwardOperation).subscribe(subscriber);
            };
            if (graphQLErrors) {
              if (!token) {
                // no user token, run query to be handled at point of execution
                logger.debug('NO USER TOKEN', graphQLErrors);
                return forwardOperation(operation);
              }
              graphQLErrors.map(async (err: any, index: number) => {
                switch (err.status) {
                  case 'UNAUTHORIZED': {
                    logger.debug('UNAUTHORIZED USER', err);

                    const retryRequest = (userObj: any) => {
                      const { headers } = operation.getContext();
                      const nextHeaders = {
                        ...headers,
                        'Application-Authorization': userObj?.token
                          ? `Bearer ${userObj.token}`
                          : null,
                      };

                      // Re-set or remove the CDN token to the new value.
                      delete nextHeaders['cdn-token'];
                      if (userObj?.cdnToken) {
                        nextHeaders['cdn-token'] = `Bearer ${userObj.cdnToken}`;
                      }

                      operation.setContext({
                        headers: nextHeaders,
                      });

                      return forwardOperation(operation);
                    };

                    const handleUserLogOut = async () => {
                      logger.debug('HANDLE USER LOGOUT');
                      await logoutHandler(client);
                      return forwardOperation(operation);
                    };
                    if (!token) {
                      await logoutHandler(client);
                    }
                    try {
                      // refresh token
                      // @ts-ignore
                      const newUser: any = await refreshUser(client);

                      if (!newUser.token) {
                        logger.debug('REFRESH NOT OK', newUser);
                        await handleUserLogOut();
                      }
                      logger.debug('REFRESH OK', newUser.token);
                      return retryRequest(newUser);
                    } catch (e) {
                      await handleUserLogOut();
                    }

                    return observer.error(new Error('Please log in'));
                  }
                  default:
                    return observer.error(graphQLErrors[index]);
                }
              });
            }

            if (networkError) {
              return observer.error(networkError);
            }
          }),
      ),
      retryLink,
      jsonLink,
      ApolloLink.split(isMutationOperation, mutationHttpLink, httpLink),
    ]),
    cache: new InMemoryCache(),
    connectToDevTools: false,
  });

  return client;
}

export function initializeApollo(
  initialState: any = null,
  config: ApolloConfig,
) {
  // If it's a preview, create a new client for each request
  if (config.preview || config.bypassCache) {
    return createApolloClient(config);
  }

  // Create the Apollo Client once in the client
  if (!apolloClients[config.key]) {
    apolloClientTimers[config.key] = Date.now();
    apolloClients[config.key] = createApolloClient(config);
    // console.log('creating client for first time: ', config.key, config.endpoint, apolloClientTimers[config.key], Date.now());
  }

  // For SSG and SSR create a new Apollo Client every 10 minutes, unless it's in build stage (process.env.CI will be "1")
  if (
    typeof window === 'undefined' &&
    typeof process.env.CI === 'undefined' &&
    process.env.npm_lifecycle_event !== 'build'
  ) {
    // TODO: Shift this to packages/next-web/lib/apollo/index.tsx so it's not polluting the shared lib
    if (Date.now() - apolloClientTimers[config.key] > apolloClientTTL) {
      // console.log('Stale client, creating a new one: ', config.key, config.endpoint, apolloClientTimers[config.key], Date.now() - apolloClientTimers[config.key], apolloClientTTL);
      apolloClientTimers[config.key] = Date.now();
      apolloClients[config.key] = createApolloClient(config);
    }
  }

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = apolloClients[config.key].extract();
    // Restore the cache using the data passed from getStaticProps/getServerSideProps combined with the existing cached data
    apolloClients[config.key].cache.restore({
      ...existingCache,
      ...initialState,
    });
  }

  // console.log('using existing client: ', config.key, config.endpoint, apolloClientTimers[config.key]);

  return apolloClients[config.key];
}

// This one's used on the client side, therefore useMemo is okay (it'll use the same client every time)
// Don't use is SSG and SSR
export function useApollo(initialState: any, config: ApolloConfig) {
  return useMemo(
    () => initializeApollo(initialState, config),
    [initialState, config],
  );
}
